import {
  toCivilDate,
  ScheduleNoteNotifyRecepients,
  ScheduleNoteRecurrenceType,
  ScheduleNoteVisibility,
  isDefined,
  isCivilDate,
} from '@mfw/common';

import type {
  ApiScheduleNoteHistory,
  UiNoteNotifyRecepients,
  UiScheduleNoteHistory,
  UiUpdateScheduleNote,
  UiCreateScheduleNote,
  UiNoteRepeatType,
  UiNoteVisibility,
  ApiScheduleNote,
  UiScheduleNote,
} from '../../schemas/scheduleNote.schema';

/**
 * UI -> API
 */
const VISIBILITY_MAP: Record<UiNoteVisibility, ScheduleNoteVisibility> = {
  always: ScheduleNoteVisibility.CLIENT_ALWAYS,
  never: ScheduleNoteVisibility.TRAINER_ONLY,
  on_the_day: ScheduleNoteVisibility.CLIENT_ON_DAY,
};

const NOTIFY_RECEPIENTS_MAP: Record<
  UiNoteNotifyRecepients,
  ScheduleNoteNotifyRecepients
> = {
  both: ScheduleNoteNotifyRecepients.BOTH,
  client: ScheduleNoteNotifyRecepients.CLIENT,
  me: ScheduleNoteNotifyRecepients.ME,
};

const RECURRENCE_MAP: Record<UiNoteRepeatType, ScheduleNoteRecurrenceType> = {
  custom: ScheduleNoteRecurrenceType.CUSTOM,
  every_n_days: ScheduleNoteRecurrenceType.EVERY_N_DAYS,
};

/**
 * API -> UI
 */
const MAP_VISIBILITY: Record<ScheduleNoteVisibility, UiNoteVisibility> = {
  [ScheduleNoteVisibility.CLIENT_ALWAYS]: 'always',
  [ScheduleNoteVisibility.CLIENT_ON_DAY]: 'on_the_day',
  [ScheduleNoteVisibility.TRAINER_ONLY]: 'never',
};

const MAP_NOTIFY_RECEPIENTS: Record<
  ScheduleNoteNotifyRecepients,
  UiNoteNotifyRecepients
> = {
  [ScheduleNoteNotifyRecepients.BOTH]: 'both',
  [ScheduleNoteNotifyRecepients.CLIENT]: 'client',
  [ScheduleNoteNotifyRecepients.ME]: 'me',
};

const MAP_RECURRENCE: Record<ScheduleNoteRecurrenceType, UiNoteRepeatType> = {
  [ScheduleNoteRecurrenceType.CUSTOM]: 'custom',
  [ScheduleNoteRecurrenceType.EVERY_N_DAYS]: 'every_n_days',
};

export const createScheduleNoteApiArgs = (ui: UiCreateScheduleNote) => {
  const payload: Record<string, unknown> = {
    clientId: ui.clientId,
    clientVisibility: VISIBILITY_MAP[ui.visibility],
    dateTime: ui.dateTime.toISOString(),
    // D1: the PICKED civil day rides with the instant
    businessDate: toCivilDate(ui.dateTime),
    sendNotification: ui.sendNotification,
    title: ui.title,
    ...(ui.description ? { description: ui.description } : {}),
    ...(ui.organizationId ? { organizationId: ui.organizationId } : {}),
  };

  if (ui.sendNotification) {
    payload.notifyRecipients = NOTIFY_RECEPIENTS_MAP[ui.notifyRecipients];
  }

  if (ui.repeat) {
    payload.recurrenceType = RECURRENCE_MAP[ui.repeatType];

    if (ui.repeatType === 'every_n_days') {
      // an emptied number field holds null; the DTO takes a number or nothing
      if (isDefined(ui.repeatInterval)) {
        payload.recurrenceInterval = ui.repeatInterval;
      }

      if (isCivilDate(ui.repeatEndDate)) {
        // Civil end day, inclusive — validated passthrough; the form state
        // already carries 'yyyy-MM-dd' (FormCivilDatePicker).
        payload.recurrenceEndDate = ui.repeatEndDate;
      }
    }

    if (ui.repeatType === 'custom' && ui.customDates.length > 0) {
      // bare civil days — the picked days verbatim (dual-mode wire)
      payload.customDates = ui.customDates.map((d) => toCivilDate(d));
    }
  }

  return payload;
};

export const mapScheduleNoteFromApi = (
  scheduleNote: ApiScheduleNote,
): UiScheduleNote => {
  return {
    ...scheduleNote,
    clientVisibility: MAP_VISIBILITY[scheduleNote.clientVisibility],
    notifyRecipients: scheduleNote.notifyRecipients
      ? MAP_NOTIFY_RECEPIENTS[scheduleNote.notifyRecipients]
      : null,
    recurrenceType: scheduleNote.recurrenceType
      ? MAP_RECURRENCE[scheduleNote.recurrenceType]
      : null,
  };
};

export const mapScheduleNoteHistoryFromApi = (
  note: ApiScheduleNoteHistory,
): UiScheduleNoteHistory => ({
  ...mapScheduleNoteFromApi(note),
  trainerName: note.trainer?.name ?? null,
});

export const updateScheduleNoteApiArgs = ({
  clientVisibility,
  dateTime,
  notifyRecipients,
  ...rest
}: UiUpdateScheduleNote) => ({
  ...rest,
  ...(dateTime
    ? {
        dateTime: dateTime.toISOString(),
        // D1: a moved instant carries its picked day
        businessDate: toCivilDate(dateTime),
      }
    : {}),
  ...(clientVisibility
    ? { clientVisibility: VISIBILITY_MAP[clientVisibility] }
    : {}),
  ...(notifyRecipients
    ? { notifyRecipients: NOTIFY_RECEPIENTS_MAP[notifyRecipients] }
    : {}),
});
